import { HttpClient } from '@adapters/HttpClient';

import { StatusApiAdapter, StatusOption } from './StatusApiAdapter';
import { ShopsApiAdapter, ShopOption } from './ShopsApiAdapter';
import {
  PaymentMethodsApiAdapter,
  PaymentMethodOption,
} from './PaymentMethodsApiAdapter';
import { ProductsApiAdapter, ProductOption } from './ProductsApiAdapter';
import { CouriersApiAdapter, CourierOption } from './CouriersApiAdapter';

export interface FilterOptions {
  statuses: StatusOption[];
  shops: ShopOption[];
  paymentMethods: PaymentMethodOption[];
  products: ProductOption[];
  couriers: CourierOption[];
}

export class FiltersApiAdapter {
  private statusAdapter: StatusApiAdapter;
  private shopsAdapter: ShopsApiAdapter;
  private paymentMethodsAdapter: PaymentMethodsApiAdapter;
  private productsAdapter: ProductsApiAdapter;
  private couriersAdapter: CouriersApiAdapter;

  constructor(private httpClient: HttpClient) {
    this.statusAdapter = new StatusApiAdapter(httpClient);
    this.shopsAdapter = new ShopsApiAdapter(httpClient);
    this.paymentMethodsAdapter = new PaymentMethodsApiAdapter(httpClient);
    this.productsAdapter = new ProductsApiAdapter(httpClient);
    this.couriersAdapter = new CouriersApiAdapter(httpClient);
  }

  async getAllFilterOptions(): Promise<FilterOptions> {
    try {
      const [statuses, shops, paymentMethods, products, couriers] =
        await Promise.all([
          this.statusAdapter.getAll(),
          this.shopsAdapter.getAll(),
          this.paymentMethodsAdapter.getAll(),
          this.productsAdapter.getAll(),
          this.couriersAdapter.getAll(),
        ]);

      return {
        statuses,
        shops,
        paymentMethods,
        products,
        couriers,
      };
    } catch (error) {
      console.error('Error loading filter options:', error);
      return {
        statuses: [],
        shops: [],
        paymentMethods: [],
        products: [],
        couriers: [],
      };
    }
  }

  async getStatuses(): Promise<StatusOption[]> {
    return this.statusAdapter.getAll();
  }

  async getShops(): Promise<ShopOption[]> {
    return this.shopsAdapter.getAll();
  }

  async getPaymentMethods(): Promise<PaymentMethodOption[]> {
    return this.paymentMethodsAdapter.getAll();
  }

  async getProducts(): Promise<ProductOption[]> {
    return this.productsAdapter.getAll();
  }

  async getCouriers(): Promise<CourierOption[]> {
    return this.couriersAdapter.getAll();
  }

  async getShipmentStatuses(): Promise<Array<{ id: number; name: string }>> {
    try {
      const raw = await this.httpClient.get<any>('/shipment-statuses');
      const list = Array.isArray(raw) ? raw : (raw?.data ?? []);
      return list.map((s: any) => ({
        id: s.id,
        name: s.name || s.description || String(s.id),
      }));
    } catch {
      return [];
    }
  }
}
